import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Printer } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { useStudentAcademicSummary } from './useStudentAcademicSummary';

interface Props {
  schoolId: string;
  schoolYearId: string;
  personId: string;
  schoolName: string;
  schoolYearName: string;
  studentName: string;
}

const PRINT_CLASS = 'printing-grade-report';

function assignmentLine(assignment: {
  state: string;
  pointsEarned: number | null;
  pointsPossible: number;
}): string {
  if (assignment.state === 'EXCUSED' || assignment.state === 'UNGRADED') return '—';
  return `${assignment.pointsEarned ?? 0} / ${assignment.pointsPossible}`;
}

function formatCredit(value: number | null | undefined): string {
  if (value == null) return '—';
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

// Printable per-student snapshot of *current* grades across every POINTS
// gradebook the student is placed in, credit-bearing or not (Principle 1).
// The on-screen part is only the print button; the report itself is portalled
// to <body> and hidden until the body carries the print class (see app.css),
// which is how ClassHealthRoster prints too.
export function StudentGradeReport({ schoolId, schoolYearId, personId, schoolName, schoolYearName, studentName }: Props) {
  const { data, loading, error } = useStudentAcademicSummary(schoolId, schoolYearId, personId);
  const summary = data?.studentAcademicSummary;
  const [printRequested, setPrintRequested] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);

  useEffect(() => {
    if (!printRequested || !summary) return;

    const leavePrintMode = () => document.body.classList.remove(PRINT_CLASS);
    const handleAfterPrint = () => {
      leavePrintMode();
      setPrintRequested(false);
    };

    document.body.classList.add(PRINT_CLASS);
    window.addEventListener('afterprint', handleAfterPrint, { once: true });
    const printTimer = window.setTimeout(() => {
      try {
        window.print();
      } catch {
        window.removeEventListener('afterprint', handleAfterPrint);
        leavePrintMode();
        setPrintError('The browser print dialog could not be opened.');
        setPrintRequested(false);
      }
    }, 0);

    return () => {
      window.clearTimeout(printTimer);
      window.removeEventListener('afterprint', handleAfterPrint);
      leavePrintMode();
    };
  }, [printRequested, summary]);

  useEffect(() => () => {
    document.body.classList.remove(PRINT_CLASS);
  }, []);

  const generatedOn = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
        <p className="text-xs text-muted-foreground">Prints a one-page snapshot of current grades and attendance.</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setPrintError(null); setPrintRequested(true); }}
          disabled={loading || !!error || !summary || printRequested}
        >
          <Printer className="h-4 w-4" />
          {printRequested ? 'Preparing report…' : 'Print grade report'}
        </Button>
      </div>
      {printError && <p role="alert" className="text-sm text-destructive print:hidden">{printError}</p>}

      {summary && createPortal(
        <div className="grade-report-print" aria-hidden="true">
          <h1>{studentName}</h1>
          <h2>{schoolName} · {schoolYearName}</h2>
          <p className="grade-report-print__meta">Current grades from posted work as of {generatedOn}. Not a final transcript.</p>
          <table>
            <thead>
              <tr>
                <th>Class</th>
                <th>Section</th>
                <th>Credit</th>
                <th>Grade</th>
                <th>Points</th>
                <th>Graded</th>
              </tr>
            </thead>
            <tbody>
              {summary.classGrades.map((grade) => (
                <tr key={`${grade.classOffering.id}-${grade.transcriptName}`}>
                  <td>
                    {grade.transcriptName}
                    {(grade.assignments ?? []).length > 0 && (
                      <ul className="grade-report-print__assignments">
                        {grade.assignments.map((assignment) => (
                          <li key={assignment.assignmentId}>
                            {assignment.name}
                            {assignment.dueOn ? ` · due ${String(assignment.dueOn)}` : ''}
                            {' · '}
                            {assignmentLine(assignment)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td>{grade.classOffering.displayName}</td>
                  <td>{formatCredit(grade.creditValue)}</td>
                  <td>
                    {grade.result.percentage != null
                      ? `${grade.result.percentage}%${grade.result.letterGrade ? ` · ${grade.result.letterGrade}` : ''}`
                      : 'Nothing graded yet'}
                  </td>
                  <td>{grade.result.earnedPoints}/{grade.result.possiblePoints}</td>
                  <td>{grade.result.includedAssignmentCount}</td>
                </tr>
              ))}
              {summary.classGrades.length === 0 && (
                <tr><td colSpan={6}>No graded classes yet.</td></tr>
              )}
            </tbody>
          </table>
          <p className="grade-report-print__attendance">
            Attendance: {summary.attendance.presentCount} present · {summary.attendance.absentCount} absent · {summary.attendance.tardyCount} tardy · {summary.attendance.excusedCount} excused
            {summary.attendance.attendanceRate != null ? ` · ${summary.attendance.attendanceRate}% attendance` : ''}
          </p>
          {(summary.attendance.days ?? []).length > 0 && (
            <table className="grade-report-print__days">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Class</th>
                  <th>Attendance</th>
                </tr>
              </thead>
              <tbody>
                {summary.attendance.days.map((day) => (
                  <tr key={`${String(day.date)}-${day.scheduleBlockId}`}>
                    <td>{String(day.date)}</td>
                    <td>{day.blockTitle}</td>
                    <td>{day.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
